import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { HTTPError } from "ky";

import { type Assessment, listAssessments } from "@/api/assessments";
import { type Asset, listAssets } from "@/api/assets";
import { listThreats, type Threat } from "@/api/threats";
import { Btn2 } from "@/components/hifi/Btn2";
import { Card } from "@/components/hifi/Card";
import { Pill } from "@/components/hifi/Pill";
import { RiskBadge, type RiskLevel } from "@/components/hifi/RiskBadge";
import { ShellLayout } from "@/components/shell/ShellLayout";

type Row = {
  threat: Threat;
  assessment: Assessment;
  asset: Asset | null;
};

function irvToLevel(irv: string | null): RiskLevel | null {
  if (!irv) return null;
  const up = irv.toUpperCase();
  if (up.includes("EXT")) return "Extreme";
  if (up.includes("HIGH")) return "High";
  if (up.includes("MOD") || up.includes("MED")) return "Moderate";
  if (up.includes("LOW")) return "Low";
  if (up.includes("NEG")) return "Negligible";
  return null;
}

function priorityVariant(
  p: string | null,
): "ok" | "warn" | "bad" | "accent" | "default" {
  if (!p) return "default";
  const up = p.toUpperCase();
  if (up === "HIGHEST" || up === "EXTREME") return "bad";
  if (up === "HIGH") return "warn";
  if (up === "MEDIUM" || up === "MODERATE") return "accent";
  if (up === "LOW" || up === "NEGLIGIBLE") return "ok";
  return "default";
}

export function ThreatsPage(): ReactElement {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState<string>("");

  async function refresh(): Promise<void> {
    setLoading(true);
    try {
      const [assessments, assets] = await Promise.all([
        listAssessments({ limit: 200 }),
        listAssets({ limit: 500 }),
      ]);
      const assetById = new Map(assets.map((a) => [a.id, a]));
      const threatLists = await Promise.all(
        assessments.map(async (a) => ({
          assessment: a,
          threats: await listThreats(a.id).catch(() => [] as Threat[]),
        })),
      );
      const flat: Row[] = [];
      for (const { assessment, threats } of threatLists) {
        for (const threat of threats) {
          flat.push({
            threat,
            assessment,
            asset: assetById.get(threat.target_asset_id) ?? null,
          });
        }
      }
      setRows(flat);
      setError(null);
    } catch (err) {
      setError(
        err instanceof HTTPError
          ? `Load failed (${String(err.response.status)})`
          : "Load failed",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(({ threat, assessment, asset }) => {
      if (q) {
        const hay = [
          threat.adversary_type,
          threat.action_type,
          threat.description ?? "",
          assessment.title,
          asset?.name ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (levelFilter) {
        const lvl = irvToLevel(threat.irv);
        if (lvl !== levelFilter) return false;
      }
      return true;
    });
  }, [rows, search, levelFilter]);

  const byLevel = useMemo(() => {
    const counts: Record<string, number> = {
      Extreme: 0,
      High: 0,
      Moderate: 0,
      Low: 0,
      Negligible: 0,
    };
    for (const { threat } of rows) {
      const lvl = irvToLevel(threat.irv);
      if (lvl && lvl in counts) counts[lvl] += 1;
    }
    return counts;
  }, [rows]);

  return (
    <ShellLayout
      active="threats"
      topbar={{
        crumbs: ["Workspace", "Threats"],
        title: "Threats",
        subtitle: `${String(rows.length)} across ${String(new Set(rows.map((r) => r.assessment.id)).size)} assessments`,
      }}
    >
      <div className="space-y-5 p-5">
        {error && (
          <div
            role="alert"
            className="rounded-2 border border-bad-bg bg-bad-bg/60 px-3 py-2 text-[12px] text-bad"
          >
            {error}
          </div>
        )}

        {/* Heat strip */}
        <div className="grid grid-cols-5 gap-3">
          {(["Extreme", "High", "Moderate", "Low", "Negligible"] as const).map(
            (lvl) => (
              <Card key={lvl} className="p-3">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[10px] font-medium uppercase tracking-[0.3px] text-n-500">
                    {lvl}
                  </div>
                  <RiskBadge level={lvl} size="sm" />
                </div>
                <div className="mt-1 font-display text-[22px] font-semibold leading-none tracking-[-0.5px] text-n-900">
                  {byLevel[lvl] ?? 0}
                </div>
              </Card>
            ),
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <input
            type="search"
            placeholder="Search adversary, action, asset, assessment…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            className="flex-1 rounded-2 border border-n-200 bg-white px-3 py-2 text-[13px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
          />
          <label className="flex items-center gap-2 text-[11px] font-medium text-n-600">
            <span className="font-mono uppercase tracking-[0.3px]">Level</span>
            <select
              value={levelFilter}
              onChange={(e) => {
                setLevelFilter(e.target.value);
              }}
              className="rounded-2 border border-n-200 bg-white px-3 py-1.5 text-[12px] text-n-900 shadow-1 focus:border-a-500 focus:outline-none"
            >
              <option value="">All</option>
              <option value="Extreme">Extreme</option>
              <option value="High">High</option>
              <option value="Moderate">Moderate</option>
              <option value="Low">Low</option>
              <option value="Negligible">Negligible</option>
            </select>
          </label>
        </div>

        <Card>
          {loading ? (
            <p className="p-4 text-[13px] text-n-500">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-[13px] text-n-500">
              No threats yet. Add threats inside an assessment's Step 2.
            </p>
          ) : filtered.length === 0 ? (
            <p className="p-4 text-[13px] text-n-500">
              No threats match the current filters.
            </p>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-n-150 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.3px] text-n-500">
                  <th className="px-4 py-2">Threat (3 A's)</th>
                  <th className="px-4 py-2">Asset</th>
                  <th className="px-4 py-2">Assessment</th>
                  <th className="px-4 py-2">L</th>
                  <th className="px-4 py-2">I</th>
                  <th className="px-4 py-2">V</th>
                  <th className="px-4 py-2">IRV</th>
                  <th className="px-4 py-2">Priority</th>
                  <th className="px-4 py-2 text-right" />
                </tr>
              </thead>
              <tbody>
                {filtered.map(({ threat, assessment, asset }) => {
                  const lvl = irvToLevel(threat.irv);
                  return (
                    <tr
                      key={threat.id}
                      className="border-b border-n-100 align-top last:border-0 hover:bg-n-50"
                    >
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-n-900">
                          {threat.adversary_type} × {threat.action_type}
                        </div>
                        {threat.description && (
                          <p className="mt-0.5 text-[11px] font-normal text-n-500">
                            {threat.description}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-[12px] text-n-700">
                        {asset?.name ?? (
                          <span className="text-n-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-[12px] text-n-700">
                        {assessment.title}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[11px] text-n-700">
                        {threat.likelihood_score ?? "—"}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[11px] text-n-700">
                        {threat.impact_score ?? "—"}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[11px] text-n-700">
                        {threat.vulnerability_rating ?? "—"}
                      </td>
                      <td className="px-4 py-2.5">
                        {lvl ? (
                          <RiskBadge level={lvl} />
                        ) : (
                          <span className="text-[11px] text-n-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {threat.risk_treatment_priority ? (
                          <Pill
                            variant={priorityVariant(
                              threat.risk_treatment_priority,
                            )}
                          >
                            {threat.risk_treatment_priority}
                          </Pill>
                        ) : (
                          <span className="text-[11px] text-n-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Btn2
                          size="xs"
                          onClick={() => {
                            void navigate({
                              to: "/assessments/$assessmentId/wizard",
                              params: { assessmentId: assessment.id },
                            });
                          }}
                        >
                          Open
                        </Btn2>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </ShellLayout>
  );
}
